const REDIRECT_MESSAGE_TYPE = 'REDIRECT_TO';

const DANGEROUS_PROTOCOL_PATTERN = /^(javascript|data|vbscript|file|blob):/i;

const LOCAL_DEVELOPMENT_HOSTNAMES = ['localhost', '127.0.0.1'];

/**
 * Returns the origin of a configured URL, or null when the URL is missing,
 * malformed, or not served over https. Plain http is accepted only for local
 * development hosts, so it can work only when the environment configuration
 * itself points to localhost.
 */
export const getTrustedOrigin = (url) => {
  if (typeof url !== 'string' || !url.trim()) {
    return null;
  }

  try {
    const { hostname, origin, protocol } = new URL(url.trim());
    const isLocalDevelopmentHost =
      LOCAL_DEVELOPMENT_HOSTNAMES.includes(hostname);

    if (
      protocol === 'https:' ||
      (protocol === 'http:' && isLocalDevelopmentHost)
    ) {
      return origin;
    }

    return null;
  } catch {
    return null;
  }
};

const uniqueOrigins = (origins) => [...new Set(origins.filter(Boolean))];

/**
 * Builds the redirect policy for the current environment from the data center
 * configuration returned by the portal API:
 * - messages are accepted only from the portal itself and the booking widget
 *   origin (dataCenter.widgetUrl),
 * - redirects are allowed only within the portal and to Travel Connect
 *   (dataCenter.travelConnectUrl).
 */
export const createRedirectPolicy = (
  dataCenter,
  currentOrigin = window.location.origin,
) => ({
  currentOrigin,
  allowedMessageOrigins: uniqueOrigins([
    currentOrigin,
    getTrustedOrigin(dataCenter?.widgetUrl),
  ]),
  allowedExternalRedirectOrigins: uniqueOrigins([
    getTrustedOrigin(dataCenter?.travelConnectUrl),
  ]).filter((origin) => origin !== currentOrigin),
});

export const isAllowedMessageOrigin = (origin, policy) => {
  if (!origin || typeof origin !== 'string' || !policy) {
    return false;
  }

  return policy.allowedMessageOrigins.includes(origin);
};

export const isValidRedirectPayload = (data) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return false;
  }

  return (
    data.type === REDIRECT_MESSAGE_TYPE &&
    typeof data.redirectLink === 'string' &&
    data.redirectLink.trim().length > 0
  );
};

export const getSafeRedirectUrl = (redirectLink, policy) => {
  if (typeof redirectLink !== 'string' || !policy) {
    return null;
  }

  const trimmedLink = redirectLink.trim();

  if (!trimmedLink || /\s/.test(trimmedLink)) {
    return null;
  }

  if (DANGEROUS_PROTOCOL_PATTERN.test(trimmedLink)) {
    return null;
  }

  if (trimmedLink.includes('\\') || trimmedLink.startsWith('//')) {
    return null;
  }

  const { currentOrigin, allowedExternalRedirectOrigins } = policy;

  try {
    const parsedUrl = trimmedLink.startsWith('/')
      ? new URL(trimmedLink, currentOrigin)
      : new URL(trimmedLink);

    if (parsedUrl.origin === currentOrigin) {
      return `${currentOrigin}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }

    if (allowedExternalRedirectOrigins.includes(parsedUrl.origin)) {
      return parsedUrl.href;
    }

    return null;
  } catch {
    return null;
  }
};

export const handleRedirectToMessage = (
  event,
  policy,
  assignLocation = (url) => {
    window.location.assign(url);
  },
) => {
  if (!isAllowedMessageOrigin(event?.origin, policy)) {
    return false;
  }

  if (!isValidRedirectPayload(event.data)) {
    return false;
  }

  const safeRedirectUrl = getSafeRedirectUrl(event.data.redirectLink, policy);

  if (!safeRedirectUrl) {
    // eslint-disable-next-line no-console
    console.warn(
      '[postMessageRedirect] Blocked REDIRECT_TO to a target that is not allowed for this environment.',
    );
    return false;
  }

  assignLocation(safeRedirectUrl);
  return true;
};
